import { Container, Graphics, Rectangle, type DestroyOptions } from 'pixi.js';
import { COLORS, DESIGN_W } from '../app/theme';
import { viewport } from '../app/viewport';
import { enterFade, LIST_PAGE, bottomBar, listHeight, pageHeader } from '../scenes/common';
import type { TicketReferenceOption } from '../scenes/ticketReferences';
import { ScrollList } from './ScrollList';
import { AccessibleButton } from './accessibility';
import { backButton, getUiIcon, makeText, Panel, sceneBackground } from './widgets';
import { hapticSelection } from '../platform/telegram';
import { toPersianDigits } from '../util/persian';

export interface TicketReferenceFieldOpts {
  width: number;
  height?: number;
  text: string;
  accessibleLabel: string;
  enabled: boolean;
  selected: boolean;
  onTap: () => void;
}

/** Large wrapped summary/control used to open the ticket's full-screen reference list. */
export class TicketReferenceField extends Container {
  private readonly a11yControl?: AccessibleButton;

  constructor(opts: TicketReferenceFieldOpts) {
    super();
    const h = opts.height ?? 96;
    const fill = opts.selected ? COLORS.panel : COLORS.panelSoft;
    this.addChild(new Panel(opts.width, h, { fill, border: true }));

    if (opts.selected) {
      this.addChild(
        new Graphics().roundRect(1.5, 1.5, opts.width - 3, h - 3, 16).stroke({ width: 3, color: COLORS.accent }),
      );
    }

    const chevron = makeText('‹', { size: 42, weight: '700', fill: opts.enabled ? COLORS.gold : COLORS.textDim, align: 'center' });
    chevron.anchor.set(0.5);
    chevron.position.set(28, h / 2);

    const text = makeText(opts.text, {
      size: opts.selected ? 22 : 24,
      weight: opts.selected ? '700' : '400',
      fill: opts.enabled ? (opts.selected ? COLORS.text : COLORS.gold) : COLORS.textDim,
    });
    text.anchor.set(1, 0.5);
    text.style.wordWrap = true;
    text.style.wordWrapWidth = opts.width - 86;
    text.position.set(opts.width - 20, h / 2);
    this.fitText(text, h - 18, 17);
    this.addChild(chevron, text);

    if (opts.enabled) {
      const activate = (): void => {
        hapticSelection();
        opts.onTap();
      };
      this.eventMode = 'static';
      this.cursor = 'pointer';
      this.hitArea = new Rectangle(0, 0, opts.width, h);
      this.on('pointertap', activate);
      this.a11yControl = new AccessibleButton(this, opts.width, h, opts.accessibleLabel, activate);
    } else {
      this.alpha = 0.72;
      this.eventMode = 'none';
    }
  }

  private fitText(text: ReturnType<typeof makeText>, maxHeight: number, minSize: number): void {
    let size = Number(text.style.fontSize);
    while (text.height > maxHeight && size > minSize) {
      size -= 1;
      text.style.fontSize = size;
    }
  }

  override destroy(options?: DestroyOptions): void {
    this.a11yControl?.destroy(options);
    super.destroy(options);
  }
}

class TicketReferenceRow extends Container {
  private readonly a11yControl: AccessibleButton;

  constructor(width: number, option: TicketReferenceOption, selected: boolean, onSelect: () => void) {
    super();
    const h = 112;
    this.addChild(new Panel(width, h, { fill: selected ? COLORS.panel : COLORS.panelSoft, border: true }));
    if (selected) {
      this.addChild(new Graphics().roundRect(1.5, 1.5, width - 3, h - 3, 16).stroke({ width: 3, color: COLORS.accent }));
    }

    const mark = new Graphics()
      .circle(width - 30, h / 2, 13)
      .fill({ color: selected ? COLORS.accent : COLORS.inputBg })
      .stroke({ width: 2, color: selected ? COLORS.accent : COLORS.fieldBorder });
    this.addChild(mark);
    if (selected) {
      const check = makeText('✓', { size: 19, weight: '700', fill: COLORS.buttonTextDark, align: 'center' });
      check.anchor.set(0.5);
      check.position.set(width - 30, h / 2 - 1);
      this.addChild(check);
    }

    const label = makeText(option.label, { size: 23, weight: selected ? '700' : '400' });
    label.anchor.set(1, 0.5);
    label.style.wordWrap = true;
    label.style.wordWrapWidth = width - 92;
    label.position.set(width - 58, h / 2);
    this.fitText(label, h - 18);
    this.addChild(label);

    const activate = (): void => {
      hapticSelection();
      onSelect();
    };
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.hitArea = new Rectangle(0, 0, width, h);
    this.on('pointertap', activate);
    this.a11yControl = new AccessibleButton(this, width, h, `انتخاب ${option.label}`, activate);
    this.a11yControl.el.setAttribute('aria-pressed', String(selected));
  }

  private fitText(text: ReturnType<typeof makeText>, maxHeight: number): void {
    let size = Number(text.style.fontSize);
    while (text.height > maxHeight && size > 17) {
      size -= 1;
      text.style.fontSize = size;
    }
  }

  override destroy(options?: DestroyOptions): void {
    this.a11yControl.destroy(options);
    super.destroy(options);
  }
}

/** Full-screen, scrollable, one-tap selector for recent games/transactions/withdrawals. */
export class TicketReferencePicker extends Container {
  constructor(
    title: string,
    options: TicketReferenceOption[],
    selectedSourceIndex: number | null,
    onSelect: (option: TicketReferenceOption) => void,
    onCancel: () => void,
  ) {
    super();
    this.addChild(sceneBackground());

    const header = pageHeader(title, onCancel, getUiIcon('ticket'));
    const hint = makeText(`${toPersianDigits(options.length)} مورد اخیر · برای انتخاب، مورد موردنظر را لمس کنید`, {
      size: 22,
      fill: COLORS.textDim,
      align: 'center',
    });
    hint.anchor.set(0.5, 0);
    hint.position.set(DESIGN_W / 2, 108);

    const topY = 154;
    const list = new ScrollList(LIST_PAGE.width, listHeight(topY, 124));
    list.position.set(LIST_PAGE.left, topY);
    if (options.length) {
      list.setItems(options.map((option) => new TicketReferenceRow(
        list.contentWidth,
        option,
        option.sourceIndex === selectedSourceIndex,
        () => onSelect(option),
      )));
    } else {
      const empty = new Container();
      empty.addChild(new Panel(list.contentWidth, 96, { fill: COLORS.panelSoft }));
      const text = makeText('موردی برای انتخاب وجود ندارد', { size: 23, fill: COLORS.textDim, align: 'center' });
      text.anchor.set(0.5);
      text.position.set(list.contentWidth / 2, 48);
      empty.addChild(text);
      list.setItems([empty]);
    }

    const back = bottomBar([backButton(onCancel)])[0];
    this.addChild(header, hint, list, back);
    enterFade([header, hint, list, back], 35);

    this.eventMode = 'static';
    this.hitArea = new Rectangle(0, 0, DESIGN_W, viewport.h);
  }
}
